import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { InputHandler } from '../../viewer/input-handler';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
type DomEvent = keyof HTMLElementEventMap | 'mousewheel';

interface DragState {
  hasPendingUp: boolean;
  pointerId: number | null;
  startX: number;
  startY: number;
  moved: boolean;
}

/* ------------------------------------------------------------------ */
/*  Defaults (can still be overridden per-call)                        */
/* ------------------------------------------------------------------ */
const DEFAULT_IGNORED: DomEvent[] = ['mouseup', 'pointerup'];
const DEFAULT_ALIAS: Partial<Record<DomEvent, DomEvent>> = {
  pointerdown: 'mousedown',
  pointerup: 'mouseup',
  pointermove: 'mousemove',
  wheel: 'mousewheel',
};
const LISTENING_EVENTS: DomEvent[] = [
  /* pointer – comes first on all modern browsers */
  'pointerdown',
  'pointerup',
  'pointercancel',
  'pointermove',
  /* legacy mouse duplicates */
  'mousedown',
  'mouseup',
  'mousemove',
  /* wheel & clicks */
  'wheel',
  'mousewheel',
  'dblclick',
  'contextmenu',
  'click',
];

/* ------------------------------------------------------------------ */
/*  Small helpers                                                      */
/* ------------------------------------------------------------------ */
const isLegacyMouse = (type: DomEvent) =>
  type === 'mousedown' || type === 'mouseup' || type === 'mousemove';

const computeWheelSign = (src: DomEvent, dst: DomEvent) =>
  src === 'wheel' && dst === 'mousewheel' ? 1 : -1;

/* ------------------------------------------------------------------ */
/*  MAIN HOOK                                                          */
/* ------------------------------------------------------------------ */
export function useForwardPointerEvents<T extends HTMLElement = HTMLElement>(
  ignored: DomEvent[] = DEFAULT_IGNORED,
  alias: Partial<Record<DomEvent, DomEvent>> = DEFAULT_ALIAS,
  targetElement?: HTMLElement,
  moveTreshold = 6,
): MutableRefObject<T | null> {
  const hostRef = useRef<T | null>(null);
  const drag = useRef<DragState>({
    hasPendingUp: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    moved: false,
  });
  const suppressMouse = useRef(false);

  /* -- Send a synthetic mouseup if we “owe” one ------------------- */
  const flushPendingUp = useCallback(() => {
    if (!drag.current.hasPendingUp) {
      return;
    }
    const canvas = targetElement ?? InputHandler.instance.domElement;
    if (!canvas) {
      return;
    }

    canvas.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
    drag.current.hasPendingUp = false;
    drag.current.pointerId = null;
  }, [targetElement]);

  /* -------------------------------------------------------------- */
  /*  Attach listeners once the element is mounted                  */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    const src = hostRef.current;
    if (!src) {
      return;
    }

    const listener = (e: Event) => {
      const type = e.type as DomEvent;

      /* 1 ───────────────── Skip duplicate legacy-mouse events */
      if (isLegacyMouse(type) && suppressMouse.current) {
        return;
      }

      /* 2 ───────────────── Maintain the suppress-flag */
      if (type === 'pointerdown') {
        suppressMouse.current = true;
      }
      if (type === 'pointerup' || type === 'pointercancel') {
        suppressMouse.current = false;
      }

      /* 3 ───────────────── Update drag-state */
      const isDown = type === 'pointerdown' || type === 'mousedown';
      const isMove = type === 'pointermove' || type === 'mousemove';
      const isUp =
        type === 'pointerup' || type === 'mouseup' || type === 'pointercancel' || type === 'click';

      if (isDown && drag.current.hasPendingUp) {
        flushPendingUp();
      }

      if (isDown) {
        drag.current = {
          hasPendingUp: true,
          pointerId: e instanceof PointerEvent ? e.pointerId : null,
          startX: (e as MouseEvent).clientX,
          startY: (e as MouseEvent).clientY,
          moved: false,
        };
      } else if (isMove && drag.current.hasPendingUp) {
        const dx = (e as MouseEvent).clientX - drag.current.startX;
        const dy = (e as MouseEvent).clientY - drag.current.startY;
        drag.current.moved ||= dx * dx + dy * dy >= moveTreshold * moveTreshold;
      } else if (isUp) {
        if (drag.current.moved) {
          flushPendingUp();
        } else {
          drag.current.hasPendingUp = false;
        }
      }

      /* 4 ───────────────── If this is an “up” after a drag,    */
      /*                     prevent overlay from intercepting   */
      if (isUp && drag.current.moved) {
        e.stopPropagation();
        e.preventDefault();
      }

      /* 5 ───────────────── Honour caller’s ignore-list */
      if (ignored.includes(type)) {
        return;
      }

      const canvas = targetElement ?? InputHandler.instance.domElement;
      if (!canvas) {
        return;
      }

      /* 6 ───────────────── Stop overlay reactions (but keep click) */
      e.stopPropagation();
      if (type === 'wheel' || type === 'mousewheel' || isMove /* pointer|mouse move */) {
        e.preventDefault();
      }

      /* 7 ───────────────── Clone & dispatch to the viewer canvas */
      const dstType = alias[type] ?? type;
      const sign = computeWheelSign(type, dstType);

      const Origin = e.constructor as new (t: string, i: any) => Event;
      const clone = new Origin(dstType, {
        bubbles: true,
        cancelable: true,
        composed: true,

        ...(e instanceof MouseEvent && {
          clientX: e.clientX,
          clientY: e.clientY,
          screenX: e.screenX,
          screenY: e.screenY,
          button: e.button,
          buttons: e.buttons,
          detail: e.detail,
          altKey: e.altKey,
          ctrlKey: e.ctrlKey,
          shiftKey: e.shiftKey,
          metaKey: e.metaKey,
        }),

        ...(e instanceof WheelEvent && {
          deltaX: sign * e.deltaX,
          deltaY: sign * e.deltaY,
          deltaZ: sign * e.deltaZ,
          deltaMode: e.deltaMode,
          wheelDelta: sign * -e.deltaY,
          wheelDeltaX: sign * -e.deltaX,
          wheelDeltaY: sign * -e.deltaY,
        }),

        ...(e instanceof PointerEvent && {
          pointerId: e.pointerId,
          pointerType: e.pointerType,
          pressure: e.pressure,
          isPrimary: e.isPrimary,
        }),
      });

      canvas.dispatchEvent(clone);
    };

    LISTENING_EVENTS.forEach((ev) => src.addEventListener(ev, listener, { passive: false }));

    /* cleanup */
    return () => {
      flushPendingUp();
      LISTENING_EVENTS.forEach((ev) => src.removeEventListener(ev, listener));
    };
  }, [ignored, alias, targetElement, moveTreshold, flushPendingUp]);

  return hostRef;
}
